import './styles/main.css';

import { CONFIG } from '@/config';
import { store } from '@/state';
import { initGlobe } from '@/visualization/globe';
import { addArc, updateArcs } from '@/visualization/arcs';
import { addEmber, updateEmbers, updateSpriteProperties } from '@/visualization/embers';
import { connectWebSocket, isWebSocketConnected } from '@/services/websocket';
import { initTimeDisplay } from '@/ui/time';
import { initEventFeed, addEventToFeed } from '@/ui/events';
import { initThreatFilter } from '@/ui/threats';
import { updateStatsDisplay, updateConnectionStatus } from '@/ui/stats';
import { updateAllSparklines } from '@/ui/sparklines';
import { initKeyboardControls } from '@/controls/keyboard';
import { initLiveFeedResize } from '@/ui/resize';
import { startDemoMode } from '@/demo';

/** Demo mode activation delay in milliseconds */
const DEMO_MODE_DELAY = 10000;

/**
 * Wait for external libraries to load
 * @param callback - Function to call when libraries are ready
 */
function waitForLibraries(callback: () => void): void {
  // Check for global libraries loaded via script tags
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const win = window as any;
  if (typeof win.THREE !== 'undefined' && typeof win.Globe !== 'undefined') {
    callback();
  } else {
    setTimeout(() => waitForLibraries(callback), 50);
  }
}

/**
 * Process pending flows from the queue
 */
function processPendingFlows(): void {
  const state = store.getState();

  if (state.flowQueueHead >= state.flowQueue.length) {
    return;
  }

  const start = performance.now();
  let processed = 0;

  while (
    state.flowQueueHead < state.flowQueue.length &&
    processed < CONFIG.maxFlowsPerFrame
  ) {
    const flow = state.flowQueue[state.flowQueueHead];
    store.update({ flowQueueHead: state.flowQueueHead + 1 });

    addArc(flow);
    addEmber(flow);
    store.incrementFlows();
    processed++;

    if (performance.now() - start > CONFIG.flowProcessBudgetMs) {
      break;
    }
  }

  // Compact queue periodically
  if (state.flowQueueHead > 1000 && state.flowQueueHead > state.flowQueue.length / 2) {
    state.flowQueue.splice(0, state.flowQueueHead);
    store.set('flowQueueHead', 0);
  }
}

/**
 * Main animation loop
 */
function animate(): void {
  requestAnimationFrame(animate);

  const state = store.getState();

  // Process flows if not paused
  if (!state.isPaused) {
    processPendingFlows();
  }

  // Update FPS counter
  const secondPassed = store.updateFPS();

  if (secondPassed) {
    // Update flow rate
    store.updateFlowRate();

    // Update sparkline data
    store.pushSparklineData();

    // Update UI
    updateStatsDisplay();
    updateAllSparklines();
  }

  // Update visualizations if not paused
  if (!state.isPaused) {
    updateArcs();
    updateEmbers();
    updateSpriteProperties();
  }
}

/**
 * Initialize the application
 */
function initApp(): void {
  console.log('FlowAtlas Mission Control loading...');

  waitForLibraries(() => {
    console.log('Libraries loaded, initializing...');

    // Initialize UI components
    initTimeDisplay();
    initEventFeed();
    initThreatFilter();
    initKeyboardControls();
    initLiveFeedResize();

    // Update connection status
    updateConnectionStatus('Connecting', 'connecting');

    // Initialize globe
    const container = document.getElementById('globe-container');
    if (container) {
      initGlobe(container);
    }

    // Connect to WebSocket
    connectWebSocket();

    // Add initial event
    addEventToFeed({
      severity: 'info',
      source: 'System',
      message: 'FlowAtlas Mission Control initialized',
    });

    // Start animation loop
    animate();

    // Start demo mode if WebSocket doesn't connect
    setTimeout(() => {
      if (!isWebSocketConnected()) {
        startDemoMode();
      }
    }, DEMO_MODE_DELAY);
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initApp);
} else {
  initApp();
}

